import { useState } from "react";
import { KeyRound, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

// Claude Code 101 labs run the real CLI against the learner's own Anthropic key.
// Checking it here takes two seconds and turns a typo into an immediate, legible
// failure instead of one discovered twenty minutes into a lab. Optional on
// purpose: anyone who would rather not paste a key into a web form can export it
// in the lab terminal, so this never blocks launching.
export const KeyPreflight = ({ slug }) => {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    if (!key.trim()) return;
    setBusy(true);
    setState(null);
    try {
      const { data } = await api.post(`/labs/${slug}/preflight-key`, { api_key: key.trim() });
      setState(data);
    } catch {
      setState({ valid: false, reason: "Could not run the check. Try again in a moment." });
    } finally {
      setBusy(false);
      setKey("");
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full mb-3 flex items-center gap-2 px-3 py-2 text-xs text-slate-600 border border-dashed border-slate-300 rounded-md hover:border-slate-400 hover:text-slate-900 transition-colors"
        data-testid="lab-key-preflight-open"
      >
        <KeyRound className="w-3.5 h-3.5 shrink-0" />
        Check your Anthropic key first (optional, 2 seconds)
      </button>
    );
  }

  return (
    <div className="mb-4 p-4 border border-slate-200 rounded-lg bg-slate-50" data-testid="lab-key-preflight">
      <p className="text-xs text-slate-600 leading-relaxed">
        This lab runs the real Claude Code CLI on your own key, so your code and usage stay
        on your account. We check it once with Anthropic and never store it.
      </p>
      <div className="mt-3 flex gap-2">
        <input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && check()}
          placeholder="sk-ant-..."
          className="flex-1 min-w-0 px-2.5 py-1.5 text-xs font-mono border border-slate-300 rounded bg-white focus:outline-none focus:border-slate-900"
          data-testid="lab-key-input"
        />
        <button
          type="button"
          onClick={check}
          disabled={busy || !key.trim()}
          className="px-3 py-1.5 text-xs font-medium bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-40 transition-colors"
          data-testid="lab-key-check"
        >
          {busy ? "Checking…" : "Check"}
        </button>
      </div>
      {state && (
        <p
          className={`mt-2.5 text-xs leading-relaxed ${state.valid ? "text-emerald-700" : "text-red-700"}`}
          data-testid="lab-key-result"
        >
          {state.valid ? "✓ " : "✗ "}
          {state.reason}
        </p>
      )}
      <a
        href="https://console.anthropic.com/settings/keys"
        target="_blank"
        rel="noreferrer"
        className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 transition-colors"
      >
        Get a key <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
};
